import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client.js";
import { MetricCard } from "../components/MetricCard.js";
import { MetricSection } from "../components/MetricSection.js";

interface ListKpisResponse {
  kpis: Array<{
    metric: string;
    current: number | null;
    previous: number | null;
    sparkline: Array<{ day: string; value: number | null }>;
  }>;
}

interface Campaign {
  id: string;
  name: string;
  type: string;
  state: string;
  status: string;
  daily_budget_micros: number | null;
  strategy_type: string | null;
  role: string;
  start_date: string | null;
  end_date: string | null;
}

interface ClassifyResult {
  total: number;
  changed: number;
  unchanged: number;
  results: Array<{
    campaign_id: number;
    name: string;
    old_role: string;
    new_role: string;
    changed: boolean;
    action_log_id: number | null;
  }>;
}

const ROLE_BADGE: Record<string, string> = {
  brand_shield: "badge-purple",
  performance_core: "badge-blue",
  hypothesis: "badge-green",
  experiment: "badge-green",
  unclassified: "badge-dim",
};

const ROLE_LABEL: Record<string, string> = {
  brand_shield: "Brand Shield",
  performance_core: "Performance Core",
  hypothesis: "Test Factory · hypothesis",
  experiment: "Test Factory · experiment",
  unclassified: "не классифицировано",
};

export function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [classifying, setClassifying] = useState(false);
  const [classifyResult, setClassifyResult] = useState<ClassifyResult | null>(null);
  const [bar, setBar] = useState<ListKpisResponse | null>(null);

  // KPI-bar: сводка по списку (Task 22). Backend stub в Task 14a, реальные числа в 14b.
  useEffect(() => {
    api<ListKpisResponse>("/api/metrics/list-kpis/campaigns")
      .then(setBar)
      .catch(() => setBar(null));
  }, []);

  function loadCampaigns() {
    setError(null);
    api<{ campaigns: Campaign[]; count: number }>("/api/campaigns/list")
      .then((d) => setCampaigns(d.campaigns))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(loadCampaigns, []);

  async function onClassify(force: boolean) {
    setClassifying(true);
    setClassifyResult(null);
    try {
      const result = await api<ClassifyResult>("/api/classify-campaigns", {
        method: "POST",
        body: JSON.stringify({ force }),
      });
      setClassifyResult(result);
      loadCampaigns();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setClassifying(false);
    }
  }

  if (error) return <div style={{ color: "var(--red)" }}>Ошибка: {error}</div>;
  if (!campaigns) return <div style={{ color: "var(--text-dim)" }}>Загрузка...</div>;

  const stateBadge = (s: string) => {
    if (s === "ON") return "badge-green";
    if (s === "OFF") return "badge-dim";
    return "badge-amber";
  };

  const unclassifiedCount = campaigns.filter((c) => c.role === "unclassified").length;

  return (
    <div>
      {bar && bar.kpis.length > 0 && (
        <MetricSection title="Сводка" cols={4}>
          {bar.kpis.slice(0, 4).map((k, i) => (
            <MetricCard
              key={i}
              metricId="cost_rub"
              current={k.current ?? null}
              previous={k.previous ?? null}
              comparisonLabel="vs прошлый период"
              sparkline={[]}
            />
          ))}
        </MetricSection>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.5rem" }}>Кампании</h2>
          <p style={{ color: "var(--text-dim)", margin: "0.25rem 0 0" }}>
            Зеркало Я.Директа · {campaigns.length} кампан
            {campaigns.length === 1 ? "ия" : campaigns.length < 5 ? "ии" : "ий"}
            {unclassifiedCount > 0 && (
              <>
                {" · "}
                <span style={{ color: "var(--amber)" }}>
                  {unclassifiedCount} не классифицирован{unclassifiedCount === 1 ? "а" : unclassifiedCount < 5 ? "ы" : "о"}
                </span>
              </>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button onClick={() => onClassify(false)} disabled={classifying}>
            {classifying ? "..." : "Классифицировать новые"}
          </button>
          <button onClick={() => onClassify(true)} disabled={classifying}>
            {classifying ? "..." : "Переклассифицировать всё"}
          </button>
        </div>
      </div>

      {classifyResult && (
        <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1.5rem", fontSize: "0.85rem" }}>
          <strong style={{ color: "var(--green)" }}>Готово.</strong>{" "}
          Обработано {classifyResult.total} · изменено {classifyResult.changed} · без изменений {classifyResult.unchanged}.
          {classifyResult.results.filter((r) => r.changed).map((r) => (
            <div key={r.campaign_id} style={{ marginTop: "0.4rem", fontSize: "0.8rem", color: "var(--text-dim)", fontFamily: "var(--mono)" }}>
              {r.campaign_id}: {r.old_role} → <strong style={{ color: "var(--green)" }}>{r.new_role}</strong>
            </div>
          ))}
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Название</th>
            <th>Тип</th>
            <th>Состояние</th>
            <th>Статус</th>
            <th>Слой</th>
            <th>Старт</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => (
            <tr key={c.id} onClick={() => navigate(`/campaigns/${c.id}`)} style={{ cursor: "pointer" }}>
              <td className="mono dim">{c.id}</td>
              <td>{c.name}</td>
              <td><span className="badge badge-blue">{c.type}</span></td>
              <td><span className={`badge ${stateBadge(c.state)}`}>{c.state}</span></td>
              <td><span className="badge badge-dim">{c.status}</span></td>
              <td>
                <span
                  className={`badge ${ROLE_BADGE[c.role] ?? "badge-dim"}`}
                  title={ROLE_LABEL[c.role] ?? c.role}
                >
                  {ROLE_LABEL[c.role] ?? c.role}
                </span>
              </td>
              <td className="mono dim">
                {c.start_date ? new Date(c.start_date).toISOString().slice(0, 10) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
